import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Card,
  Row,
  Col,
  Button,
  Space,
  Spin,
  Empty,
  Pagination,
  Typography,
  Image,
  Tooltip,
  Input,
  Select,
  Segmented,
  message,
  Checkbox,
  Modal,
  InputNumber,
  Progress,
  Alert,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  ShoppingCartOutlined,
  EyeOutlined,
  SyncOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  CheckSquareOutlined,
  CloseSquareOutlined,
} from '@ant-design/icons';
import { getProductList, getProductStatistics, syncProducts, batchCreateListings } from '../../api';
import { useSupplier } from '../../context/SupplierContext';
import { SUPPLIER_LABELS } from '../../types/supplier';
import { formatPrice, calculateSellingPrice } from '../../utils/price';
import VirtualProductGrid from '../../components/VirtualProductGrid';
import { ProductGridSkeleton, StatisticsSkeleton } from '../../components/ProductCardSkeleton';
import type { GigaProductFull, ProductStatistics, CreateListingRequest } from '../../types';

const { Title, Text } = Typography;
const { Search } = Input;

const getStatisticValue = (
  statistics: ProductStatistics,
  primaryKey: keyof ProductStatistics,
  fallbackKey: keyof ProductStatistics,
) => {
  const value = statistics[primaryKey] ?? statistics[fallbackKey] ?? 0;
  return typeof value === 'number' ? value : 0;
};

const ProductList: React.FC = () => {
  const navigate = useNavigate();
  const { supplier } = useSupplier();
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [products, setProducts] = useState<GigaProductFull[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [onlyInStock, setOnlyInStock] = useState(false);
  const [listingState, setListingState] = useState<string>('unlisted');  // 默认显示未上架
  const [statistics, setStatistics] = useState<ProductStatistics | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'virtual'>('grid');
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  
  // 批量上架相关状态
  const [selectMode, setSelectMode] = useState(false);
  const [selectedSkus, setSelectedSkus] = useState<Set<string>>(new Set());
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [batchUploading, setBatchUploading] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0, success: 0, failed: 0 });
  const [profitMargin, setProfitMargin] = useState(30); // 默认利润率 30%
  
  // 用于取消请求的 AbortController
  const abortControllerRef = useRef<AbortController | null>(null);
  const productRequestSeqRef = useRef(0);
  const statisticsRequestSeqRef = useRef(0);

  useEffect(() => {
    setProducts([]);
    setTotal(0);
    setStatistics(null);
    setListError(null);
    setSelectedSkus(new Set());
    setIsFirstLoad(true);
    setPage(1);
  }, [supplier]);

  // 加载产品列表（现在直接返回聚合数据，无需多次请求）
  const loadProducts = useCallback(async () => {
    // 取消之前的请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // 创建新的 AbortController
    abortControllerRef.current = new AbortController();
    const requestSeq = productRequestSeqRef.current + 1;
    productRequestSeqRef.current = requestSeq;
    
    setLoading(true);
    setListError(null);
    try {
      const res = await getProductList({
        page,
        pageSize,
        search: searchTerm || undefined,
        onlyInStock,
        listingState: listingState || undefined,
      }, supplier);

      if (res.success && res.data) {
        if (productRequestSeqRef.current !== requestSeq) return;
        const { records, pageInfo } = res.data;
        setProducts(records);
        setTotal(pageInfo.totalNum);
      } else {
        if (productRequestSeqRef.current !== requestSeq) return;
        const errorMessage = res.error || res.msg || `获取${SUPPLIER_LABELS[supplier]}产品列表失败`;
        message.error(errorMessage);
        setProducts([]);
        setTotal(0);
        setListError(errorMessage);
      }
    } catch (error) {
      // 忽略取消的请求
      if (axios.isCancel(error)) {
        return;
      }
      console.error('加载产品失败:', error);
      if (productRequestSeqRef.current !== requestSeq) return;
      const errorMessage = `加载${SUPPLIER_LABELS[supplier]}产品失败，请检查接口配置或网络连接`;
      message.error(errorMessage);
      setProducts([]);
      setTotal(0);
      setListError(errorMessage);
    } finally {
      if (productRequestSeqRef.current === requestSeq) {
        setLoading(false);
        setIsFirstLoad(false);
      }
    }
  }, [page, pageSize, searchTerm, onlyInStock, listingState, supplier]);

  const loadStatistics = useCallback(async () => {
    const requestSeq = statisticsRequestSeqRef.current + 1;
    statisticsRequestSeqRef.current = requestSeq;
    try {
      const res = await getProductStatistics(undefined, supplier);
      if (res.success && res.data) {
        if (statisticsRequestSeqRef.current !== requestSeq) return;
        setStatistics(res.data);
      } else if (statisticsRequestSeqRef.current === requestSeq) {
        setStatistics(null);
      }
    } catch (error) {
      // 忽略取消的请求
      if (axios.isCancel(error)) {
        return;
      }
      console.error('加载统计信息失败:', error);
      if (statisticsRequestSeqRef.current === requestSeq) {
        setStatistics(null);
      }
    }
  }, [supplier]);

  useEffect(() => {
    loadProducts();
    
    // 组件卸载时取消请求
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadProducts]);

  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  // 手动同步产品
  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await syncProducts(undefined, supplier);
      if (res.success && res.data) {
        message.success(`同步完成: ${res.data.synced} 个产品, ${res.data.errors} 个错误`);
        loadProducts();
        loadStatistics();
      } else {
        const errorMessage = res.error || `${SUPPLIER_LABELS[supplier]}同步失败`;
        setListError(errorMessage);
        setProducts([]);
        setTotal(0);
        message.error(errorMessage);
      }
    } catch {
      const errorMessage = `${SUPPLIER_LABELS[supplier]}同步失败，请重试`;
      setListError(errorMessage);
      setProducts([]);
      setTotal(0);
      message.error(errorMessage);
    } finally {
      setSyncing(false);
    }
  };

  // 搜索
  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  // 批量选择相关
  const toggleSelectMode = () => {
    if (selectMode) {
      // 退出选择模式时清空选中
      setSelectedSkus(new Set());
    }
    setSelectMode(!selectMode);
  };

  const toggleProductSelection = (sku: string) => {
    setSelectedSkus((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sku)) {
        newSet.delete(sku);
      } else {
        newSet.add(sku);
      }
      return newSet;
    });
  };

  const selectAllOnPage = () => {
    const newSet = new Set(selectedSkus);
    products.forEach((p) => newSet.add(p.sku));
    setSelectedSkus(newSet);
  };

  const deselectAllOnPage = () => {
    const newSet = new Set(selectedSkus);
    products.forEach((p) => newSet.delete(p.sku));
    setSelectedSkus(newSet);
  };

  // 批量上架
  const handleBatchList = async () => {
    if (selectedSkus.size === 0) {
      message.warning('请先选择要上架的产品');
      return;
    }
    setBatchModalVisible(true);
  };

  const executeBatchList = async () => {
    const selectedProducts = products.filter((p) => selectedSkus.has(p.sku));
    if (selectedProducts.length === 0) {
      message.warning('未找到选中的产品数据');
      return;
    }

    setBatchUploading(true);
    setBatchProgress({ current: 0, total: selectedProducts.length, success: 0, failed: 0 });

    // 构建批量上架请求
    const items: CreateListingRequest[] = selectedProducts.map((product) => {
      const bestPrice = product.discountedPrice && product.discountedPrice > 0 && product.discountedPrice < product.price
        ? product.discountedPrice
        : product.price;
      const sellingPrice = calculateSellingPrice(bestPrice, product.shippingFee, profitMargin);

      return {
        giga_sku: product.sku,
        giga_product_name: product.productName,
        giga_main_image: product.mainImage || '',
        giga_category: product.category || '',
        giga_price: bestPrice,
        giga_shipping_fee: product.shippingFee,
        giga_map_price: product.mapPrice || undefined,
        selling_price: sellingPrice,
        custom_title: product.productName,
        shopify_product_type: product.category || '',
        giga_available_qty: product.availableQty || 0,
      };
    });

    try {
      const res = await batchCreateListings(items, supplier);
      if (res.success && res.data) {
        const { success, failed, errors } = res.data;
        setBatchProgress({
          current: success + failed,
          total: items.length,
          success,
          failed,
        });
        
        if (failed > 0 && errors && errors.length > 0) {
          message.warning(`批量上架完成：成功 ${success} 个，失败 ${failed} 个`);
          console.log('批量上架错误详情:', errors);
        } else {
          message.success(`批量上架成功：${success} 个产品已添加到上架列表`);
        }
        
        // 清空选中并退出选择模式
        setSelectedSkus(new Set());
        setSelectMode(false);
        setBatchModalVisible(false);
      } else {
        message.error(res.error || '批量上架失败');
      }
    } catch {
      message.error('批量上架请求失败，请重试');
    } finally {
      setBatchUploading(false);
    }
  };

  const getInventoryDisplay = (qty: number) => {
    if (qty === 0) {
      return { text: '缺货', color: '#ff4d4f' };
    }
    if (qty < 10) {
      return { text: `仅剩 ${qty}`, color: '#faad14' };
    }
    return { text: `${qty} 件`, color: '#52c41a' };
  };

  const ProductCard: React.FC<{ product: GigaProductFull }> = ({ product }) => {
    const inventoryInfo = getInventoryDisplay(product.availableQty);
    // 注意：必须使用 Boolean() 或 !! 确保返回布尔值，否则当 discountedPrice=0 时会渲染 "0"
    const hasDiscount = Boolean(product.discountedPrice && product.discountedPrice > 0 && product.discountedPrice < product.price);
    const bestPrice = hasDiscount ? product.discountedPrice : product.price;
    const isSelected = selectedSkus.has(product.sku);

    return (
      <Card
        className="product-card"
        hoverable
        onClick={selectMode ? () => toggleProductSelection(product.sku) : undefined}
        style={selectMode && isSelected ? { border: '2px solid #4f6ef7', boxShadow: '0 0 15px rgba(79, 110, 247, 0.4)' } : undefined}
        cover={
          <div style={{ padding: 12, background: 'rgba(0,0,0,0.2)', position: 'relative', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {selectMode && (
              <Checkbox
                checked={isSelected}
                onChange={() => toggleProductSelection(product.sku)}
                onClick={(e) => e.stopPropagation()}
                style={{ position: 'absolute', top: 8, left: 8, zIndex: 10 }}
              />
            )}
            <Image
              src={product.mainImage || 'https://via.placeholder.com/200x200?text=No+Image'}
              alt={product.productName}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              fallback="https://via.placeholder.com/200x200?text=Error"
              preview={false}
            />
          </div>
        }
        actions={selectMode ? undefined : [
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/products/${product.sku}?qty=${product.availableQty}`)}
            style={{ color: '#a0b1c5' }}
          >
            详情
          </Button>,
          <Button
            type="text"
            size="small"
            icon={<ShoppingCartOutlined />}
            onClick={() => navigate(`/products/${product.sku}?action=list&qty=${product.availableQty}`)}
            style={{ color: '#a0b1c5' }}
          >
            上架
          </Button>,
        ]}
      >
        <Card.Meta
          title={
            <Tooltip title={product.productName}>
              <Text strong style={{ width: '100%', color: '#fff', fontSize: 14 }}>
                {product.productName}
              </Text>
            </Tooltip>
          }
          description={
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
              <Text style={{ fontSize: 12, color: '#a0b1c5' }}>
                SKU: {product.sku}
              </Text>
              <div>
                <span style={{ fontSize: 12, color: inventoryInfo.color, fontWeight: 500 }}>
                  {inventoryInfo.text}
                </span>
              </div>
              <div style={{ marginTop: 8 }}>
                <span className="price-tag">
                  {formatPrice(bestPrice || 0)}
                </span>
                {hasDiscount && (
                  <span className="price-original">
                    {formatPrice(product.price)}
                  </span>
                )}
              </div>
              {product.shippingFee > 0 && (
                <Text style={{ fontSize: 12, color: '#a0b1c5' }}>
                  运费: {formatPrice(product.shippingFee)}
                </Text>
              )}
              {product.accountName && (
                <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>
                  来源: {product.accountName}
                </Text>
              )}
            </Space>
          }
        />
      </Card>
    );
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ color: '#fff', marginBottom: 8 }}>产品浏览</Title>
        <div style={{ 
          background: 'rgba(255, 255, 255, 0.03)', 
          padding: '12px 20px', 
          borderRadius: 12, 
          border: '1px solid rgba(255, 255, 255, 0.08)',
          display: 'inline-block'
        }}>
          <Text style={{ color: '#a0b1c5' }}>
            浏览{SUPPLIER_LABELS[supplier]}产品，选择感兴趣的产品上架到您的 Shopify 店铺
            {statistics ? (
              <span style={{ marginLeft: 16 }}>
                共 <strong style={{ color: '#fff' }}>{getStatisticValue(statistics, 'totalProducts', 'total')}</strong> 个产品，
                有库存 <strong style={{ color: '#52c41a' }}>{getStatisticValue(statistics, 'inStockProducts', 'inStock')}</strong>，
                缺货 <strong style={{ color: '#ff4d4f' }}>{getStatisticValue(statistics, 'outOfStockProducts', 'outOfStock')}</strong>
              </span>
            ) : (
              <span style={{ marginLeft: 16 }}>
                <StatisticsSkeleton />
              </span>
            )}
          </Text>
        </div>
      </div>

      {/* 筛选区域 */}
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Search
            placeholder="搜索 SKU 或产品名称"
            allowClear
            style={{ width: 300 }}
            onSearch={handleSearch}
            enterButton={<SearchOutlined />}
          />
          <Select
            placeholder="库存筛选"
            style={{ width: 120 }}
            value={onlyInStock ? 'inStock' : 'all'}
            onChange={(value) => {
              setOnlyInStock(value === 'inStock');
              setPage(1);
            }}
          >
            <Select.Option value="all">全部</Select.Option>
            <Select.Option value="inStock">仅有库存</Select.Option>
          </Select>
          <Select
            placeholder="上架状态"
            style={{ width: 120 }}
            value={listingState || 'all'}
            onChange={(value) => {
              setListingState(value === 'all' ? '' : value);
              setPage(1);
            }}
          >
            <Select.Option value="all">全部</Select.Option>
            <Select.Option value="unlisted">未上架</Select.Option>
            <Select.Option value="listed">已上架</Select.Option>
            <Select.Option value="draft">待上架</Select.Option>
            <Select.Option value="delisted">已下架</Select.Option>
            <Select.Option value="error">上架失败</Select.Option>
          </Select>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              setSearchTerm('');
              setOnlyInStock(false);
              setListingState('unlisted');
              setPage(1);
            }}
          >
            重置
          </Button>
          <Button
            icon={<SyncOutlined spin={syncing} />}
            onClick={handleSync}
            loading={syncing}
          >
            同步产品
          </Button>
          <Segmented
            value={viewMode}
            onChange={(value) => setViewMode(value as 'grid' | 'virtual')}
            options={[
              { value: 'grid', icon: <AppstoreOutlined />, label: '普通' },
              { value: 'virtual', icon: <UnorderedListOutlined />, label: '虚拟滚动' },
            ]}
          />
          
          {/* 批量上架操作 */}
          <div style={{ borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: 16, marginLeft: 8 }}>
            <Button
              type={selectMode ? 'primary' : 'default'}
              icon={selectMode ? <CloseSquareOutlined /> : <CheckSquareOutlined />}
              onClick={toggleSelectMode}
            >
              {selectMode ? '退出选择' : '批量选择'}
            </Button>
            {selectMode && (
              <>
                <Button onClick={selectAllOnPage} style={{ marginLeft: 8 }}>
                  全选本页
                </Button>
                <Button onClick={deselectAllOnPage} style={{ marginLeft: 8 }}>
                  取消本页
                </Button>
                <Button
                  type="primary"
                  icon={<ShoppingCartOutlined />}
                  onClick={handleBatchList}
                  disabled={selectedSkus.size === 0}
                  style={{ marginLeft: 8 }}
                >
                  批量上架 ({selectedSkus.size})
                </Button>
              </>
            )}
          </div>
        </Space>
      </Card>

      {listError && (
        <Alert
          type="error"
          showIcon
          message={`${SUPPLIER_LABELS[supplier]}接口加载失败`}
          description={listError}
          action={
            <Button size="small" danger onClick={loadProducts}>
              重试
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {/* 产品列表 */}
      {/* 首次加载时显示骨架屏 */}
      {isFirstLoad && loading ? (
        <ProductGridSkeleton count={12} />
      ) : (
        <Spin spinning={loading}>
          {products.length > 0 ? (
            <>
              {viewMode === 'virtual' ? (
              // 虚拟滚动模式 - 适合大数据量
              <Card bodyStyle={{ padding: 16 }}>
                <VirtualProductGrid
                  products={products}
                  containerHeight={600}
                  columnCount={4}
                />
              </Card>
            ) : (
              // 普通网格模式
              <Row gutter={[16, 16]}>
                {products.map((product) => (
                  <Col xs={24} sm={12} md={8} lg={6} xl={4} key={product.sku}>
                    <ProductCard product={product} />
                  </Col>
                ))}
              </Row>
            )}

            <div
              style={{
                marginTop: 24,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Pagination
                current={page}
                pageSize={pageSize}
                total={total}
                showSizeChanger
                showQuickJumper
                pageSizeOptions={['20', '50', '100', '200']}
                showTotal={(total) => `共 ${total} 个产品`}
                onChange={(p, ps) => {
                  setPage(p);
                  setPageSize(ps);
                }}
              />
            </div>
          </>
          ) : (
            !loading && (
              <Empty
                description="暂无产品数据"
                style={{ marginTop: 100 }}
              >
                <Button type="primary" onClick={loadProducts}>
                  重新加载
                </Button>
              </Empty>
            )
          )}
        </Spin>
      )}

      {/* 批量上架确认弹窗 */}
      <Modal
        title="批量上架确认"
        open={batchModalVisible}
        onCancel={() => !batchUploading && setBatchModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setBatchModalVisible(false)} disabled={batchUploading}>
            取消
          </Button>,
          <Button key="submit" type="primary" loading={batchUploading} onClick={executeBatchList}>
            确认上架
          </Button>,
        ]}
        closable={!batchUploading}
        maskClosable={!batchUploading}
      >
        <div style={{ marginBottom: 16 }}>
          <Text>
            已选择 <strong style={{ color: '#4f6ef7' }}>{selectedSkus.size}</strong> 个产品进行批量上架
          </Text>
        </div>
        
        <div style={{ marginBottom: 16 }}>
          <Text style={{ display: 'block', marginBottom: 8 }}>利润率设置：</Text>
          <Space>
            <InputNumber
              min={0}
              max={200}
              value={profitMargin}
              onChange={(value) => setProfitMargin(value || 30)}
              addonAfter="%"
              style={{ width: 120 }}
            />
            <Text type="secondary">
              销售价 = (成本价 + 运费) × (1 + 利润率)
            </Text>
          </Space>
        </div>

        {batchUploading && (
          <div style={{ marginTop: 16 }}>
            <Progress
              percent={Math.round((batchProgress.current / batchProgress.total) * 100)}
              status={batchProgress.failed > 0 ? 'exception' : 'active'}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">
                进度: {batchProgress.current}/{batchProgress.total}，
                成功: <span style={{ color: '#52c41a' }}>{batchProgress.success}</span>，
                失败: <span style={{ color: '#ff4d4f' }}>{batchProgress.failed}</span>
              </Text>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ProductList;
